import { Button } from '@herit-fe/heritage-ui';
import type { PlantContract, RevenueDisplayType } from '@/shared/data-types';
import { formatInteger, getFixedUnitPrice, SUFFIX } from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';

const EMPTY = UI_TEXT.COMMON_EMPTY_VALUE;

function ContractFooter({
  displayType,
  contract,
  onInput,
}: {
  displayType: RevenueDisplayType;
  contract: PlantContract | null;
  onInput: () => void;
}) {
  if (displayType === 'SELF_NOT_SET') {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{UI_TEXT.REVENUE_CONTRACT_TITLE}</p>
          <p className="text-xs text-muted-foreground">{UI_TEXT.REVENUE_CONTRACT_SELF_NOT_SET}</p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={onInput}>
          {UI_TEXT.REVENUE_CONTRACT_INPUT}
        </Button>
      </div>
    );
  }
  if (displayType === 'SELF_SET') {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{UI_TEXT.REVENUE_CONTRACT_TITLE}</p>
          <p className="text-xs text-muted-foreground">{UI_TEXT.REVENUE_CONTRACT_SELF_SET}</p>
        </div>
        {contract?.contractName ? (
          <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {contract.contractName}
          </span>
        ) : null}
      </div>
    );
  }
  // SALE_FIXED_SET — 계약방식 + 고정단가.
  const priceLabel =
    contract?.contractCode === 'P101'
      ? UI_TEXT.REVENUE_CONTRACT_PRICE_P101
      : UI_TEXT.REVENUE_CONTRACT_PRICE_P103;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{UI_TEXT.TERM_CONTRACT_METHOD}</span>
        <span className="text-sm font-medium text-foreground">
          {contract?.contractName ?? EMPTY}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{priceLabel}</span>
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {formatInteger(getFixedUnitPrice(contract), SUFFIX.WON)}
        </span>
      </div>
    </div>
  );
}

/**
 * 수익 계약 Footer — 자가소비(미설정·설정)·고정단가일 때만 계약 정보를 카드 하단에 붙인다. 그 외(전체=null /
 * 미등록 / 발전사업 현물)는 시장가를 **카드 밖 상단 `MarketCard`** 가 담당하므로 이 영역을 안 그린다.
 * 구앱 `getFooterSlot` + `Card.Footer` 이식 — 상단 divider + subtle 톤, 자체 테두리·radius 없음(카드 border/모서리 공유).
 */
export function RevenueFooter({
  displayType,
  contract,
  onContractInput,
}: {
  displayType: RevenueDisplayType | null;
  contract: PlantContract | null;
  onContractInput: () => void;
}) {
  const content =
    displayType === 'SELF_NOT_SET' ||
    displayType === 'SELF_SET' ||
    displayType === 'SALE_FIXED_SET' ? (
      <ContractFooter displayType={displayType} contract={contract} onInput={onContractInput} />
    ) : null;

  if (!content) return null;
  return <div className="border-t border-border bg-muted/40 px-4 py-3">{content}</div>;
}
